import React from 'react'
import pick from 'lodash/pick'

import {
  IntakeAnswer,
  IntakeMessageTypeEnum,
  MessagePropsUnion,
} from 'APP/Services/Mattermost/types'

// Components
import IntakeSingleChoice from '../IntakeSingleChoice'
import IntakeText from '../IntakeText'
import IntakeMultiChoice from '../IntakeMultiChoice'
import IntakeSearchChoice from '../IntakeSearchChoice'
import IntakeAddressSearch from '../IntakeAddressSearch'
import IntakePhoto from '../IntakePhoto'
import IntakeImageMap from '../IntakeImageMap'
import IntakeBooking from '../IntakeBooking'
import IntakeProviderList from '../IntakeProviderList'
import IntakeMultiSubPrompt from '../IntakeMultiSubPrompt'
import IntakeCheckout from '../IntakeCheckout'

type TransformerFn<T extends readonly unknown[] = readonly unknown[]> = (...args: T) => IntakeAnswer

type SubmitHandler<T extends readonly unknown[] = readonly unknown[]> = (...args: T) => void

type SendAnswerFn = (answer: Record<string, unknown>) => void

type IntakeReplyFormProps = Partial<MessagePropsUnion> & {
  sendAnswer: SendAnswerFn
}

function createTransformedSubmitHandler<T extends readonly unknown[]>(
  props: Partial<MessagePropsUnion>,
  sendAnswerFn: SendAnswerFn,
  transformerFn: TransformerFn<T>
): SubmitHandler<T> {
  const baseProps = pick(props, [
    'member_app_fullscreen',
    'member_app_visible',
    'provider_app_visible',
  ])

  return (...args: T) => {
    const answer = transformerFn(...args)

    sendAnswerFn({
      ...answer,
      props: {
        type: `${props.type}_response`,
        ...answer.props,
        ...baseProps,
      },
    })
  }
}

export const REPLY_FORMS = {
  [IntakeMessageTypeEnum.prompt_text]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (message: string) => ({
        display: message,
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeText,
  },
  [IntakeMessageTypeEnum.prompt_choice_single]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (choice: { display: string }) => ({
        display: choice.display,
        props: {
          selected_choice: choice,
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeSingleChoice,
  },
  [IntakeMessageTypeEnum.prompt_choice_multi]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (choices: Array<{ display: string }>) => ({
        display: choices.map(({ display }) => display).join(', '),
        props: {
          selected_choices: choices,
          ...pick(props, ['payload', 'minimum_selection', 'maximum_selection']),
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeMultiChoice,
  },
  [IntakeMessageTypeEnum.prompt_choice_search]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (choice: { display: string }) => ({
        display: choice.display,
        props: {
          selected_choice: choice,
          ...pick(props, ['payload', 'group_data']),
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeSearchChoice,
  },
  [IntakeMessageTypeEnum.prompt_address]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (place: { address: string }) => ({
        display: place.address,
        props: {
          selected_address: place,
          ...pick(props, ['payload', 'place_type']),
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeAddressSearch,
  },
  [IntakeMessageTypeEnum.prompt_choice_image_map]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (
        choices: Array<{ display: string }>,
        image_metadata: Record<string, unknown>
      ) => ({
        display: choices.map(({ display }) => display).join(', '),
        props: {
          selected_choices: choices,
          image_metadata,
          ...pick(props, ['payload', 'image_source']),
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeImageMap,
  },
  [IntakeMessageTypeEnum.prompt_booking]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = ({
        display,
        payload,
      }: {
        display: string
        payload: Record<string, unknown>
      }) => ({
        display,
        props: {
          ...payload,
          ...pick(props, ['payload', 'member_id']),
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeBooking,
  },
  [IntakeMessageTypeEnum.prompt_file]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (fileObj: unknown) => ({
        fileObj,
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    silent: true,
    component: IntakePhoto,
  },
  [IntakeMessageTypeEnum.prompt_provider_list]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (provider: { display: string }) => ({
        display: provider.display,
        props: {
          selected_choice: provider,
        },
      })

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeProviderList,
  },
  [IntakeMessageTypeEnum.prompt_multi_subprompt]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = (promptAnswers: unknown, display: string) => {
        const { payload } = props
        return {
          display,
          props: {
            payload,
            responses: promptAnswers,
          },
        }
      }

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeMultiSubPrompt,
  },
  [IntakeMessageTypeEnum.prompt_checkout]: {
    createSubmitHandler: (props: Partial<MessagePropsUnion>, sendAnswerFn: SendAnswerFn) => {
      const transformerFn = ({
        display,
        payload,
      }: {
        display: string
        payload: Record<string, unknown>
      }) => {
        return {
          display,
          props: {
            ...payload,
            ...pick(props, ['payload']),
          },
        }
      }

      return createTransformedSubmitHandler(props, sendAnswerFn, transformerFn)
    },
    component: IntakeCheckout,
  },
}

const IntakeReplyForm: React.FC<IntakeReplyFormProps> = ({ sendAnswer, ...rest }) => {
  const replyFormConfig = REPLY_FORMS[rest.type as keyof typeof REPLY_FORMS]

  if (
    replyFormConfig &&
    replyFormConfig.component &&
    typeof replyFormConfig.createSubmitHandler === 'function'
  ) {
    const onSendAnswer = replyFormConfig.createSubmitHandler(rest, sendAnswer)
    const Component = replyFormConfig.component as React.ComponentType<
      Partial<MessagePropsUnion> & {
        sendAnswer: ReturnType<typeof replyFormConfig.createSubmitHandler>
      }
    >

    return <Component {...rest} sendAnswer={onSendAnswer} />
  }

  return null
}

export default IntakeReplyForm
